"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar as CalendarIcon, Loader, CircleCheck, CircleAlert } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/20 rounded-lg flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Message Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md px-8 border-primary/50 text-primary hover:bg-primary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-wider text-muted-foreground font-default">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Alex" className="rounded-md bg-background/40 border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_4px_12px_-4px_rgba(0,255,255,0.3)] transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-wider text-muted-foreground font-default">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Rivers" className="rounded-md bg-background/40 border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_4px_12px_-4px_rgba(0,255,255,0.3)] transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-wider text-muted-foreground font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="alex@company.com" className="rounded-md bg-background/40 border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_4px_12px_-4px_rgba(0,255,255,0.3)] transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-wider text-muted-foreground font-default">Company Name</FormLabel>
              <FormControl>
                <Input type="text" placeholder="DataZen Solutions" className="rounded-md bg-background/40 border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_4px_12px_-4px_rgba(0,255,255,0.3)] transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consultationType" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-wider text-muted-foreground font-default">Service Interest</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="rounded-md bg-background/40 border-0 border-b border-white/10 focus:ring-0 focus:border-primary transition-all duration-300">
                    <SelectValue placeholder="Select a solution" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="automation">AI Workflow Automation</SelectItem>
                  <SelectItem value="dashboards">Business Dashboards</SelectItem>
                  <SelectItem value="document">AI Document Assistants</SelectItem>
                  <SelectItem value="consulting">Automation Consulting</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferredDate" rules={{
        required: "Please select a preferred date"
      }} render={({
        field
      }) => <FormItem className="flex flex-col justify-end">
              <FormLabel className="text-[12px] uppercase tracking-wider text-muted-foreground font-default mb-2">Preferred Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal rounded-md bg-background/40 border-0 border-b border-white/10 hover:bg-background/60 focus:ring-0 focus:border-primary transition-all duration-300", !field.value && "text-muted-foreground")}>
                      {field.value ? new Date(field.value).toLocaleDateString(undefined, {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                }) : <span>Pick a date</span>}
                      <SafeHydrate><CalendarIcon className="ml-auto size-4 opacity-50 text-primary" /></SafeHydrate>
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                  <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date < new Date() || date < new Date("1900-01-01")} initialFocus /></SafeHydrate>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-md bg-gradient-to-r from-primary to-chart-2 text-primary-foreground hover:opacity-90 hover:shadow-[0_0_30px_rgba(0,255,255,0.4)] transition-all duration-300 py-6 text-lg font-default font-semibold">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Initializing Connection...
          </> : 'Send Message'}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground mt-4 font-default">
        Enterprise-grade security. Your data is encrypted and never shared.
      </p>
    </Form>;
}
export default function StartYourTransformation() {
  return <section data-section-id="253510" id="start-your-transformation" className="py-32 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/5 rounded-lg blur-[120px] -z-10 pointer-events-none" />
      
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-default font-bold text-foreground mb-6 tracking-tighter">
            Start Your Transformation
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-default">
            Ready to automate your operations? Connect with our AI specialists to design your custom workflow solution.
          </p>
        </div>

        <Card className="bg-white/5 backdrop-blur-[40px] shadow-2xl border border-white/10 rounded-xl overflow-hidden">
          <CardHeader className="pb-0 pt-8 px-8 md:px-12">
            <CardTitle className="sr-only">DataZen Contact Form</CardTitle>
            <CardDescription className="sr-only">Provide your details to initiate a consultation with our automation experts.</CardDescription>
          </CardHeader>
          <CardContent className="p-8 md:p-12 pt-6">
            <FormProvider formKey="transformation_form_home" formId="transformation_form_home" sectionName="start-your-transformation" defaultValues={{
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            consultationType: "",
            preferredDate: ""
          }} submitText="Send Message" successMessage="Thank you for reaching out. A DataZen automation consultant will review your request and contact you within 24 hours.">
              <ConsultationFormInner />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}